import * as React from "react";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { InputWrapper, INVALID_CONTROL_CLASS } from "./input-wrapper";
import { cn } from "@/dentlab/src/lib/util";

const EMPTY_SENTINEL = "__empty__";

type SelectDensitySize = "sm" | "md" | "lg";

const SELECT_SIZE_CLASS: Record<SelectDensitySize, string> = {
	sm: "h-8 text-sm",
	md: "h-10",
	lg: "h-12",
};

interface CustomSelectProps {
	options: {
		value: string | number;
		label: string;
	}[];
	inputLabel: string;
	value?: string | number | null;
	onChange: (value: string | number | null) => void;
	helperText?: string;
	disabled?: boolean;
	width?: string;
	required?: boolean;
	className?: string;
	placeholder?: string;
	triggerClassName?: string;
	tabIndex?: number;
	densitySize?: SelectDensitySize;
	inputHeight?: number;
	validationError?: string | null;
	"aria-label"?: string;
	"data-testid"?: string;
}

export const CustomSelectShadcn: React.FC<CustomSelectProps> = ({
	options,
	inputLabel,
	value,
	onChange,
	helperText = "",
	disabled = false,
	required = false,
	className,
	placeholder,
	triggerClassName,
	tabIndex,
	densitySize,
	inputHeight,
	validationError,
	"aria-label": ariaLabel,
	"data-testid": dataTestId,
}) => {
	const selectValue =
		value === "" || value == null ? EMPTY_SENTINEL : value.toString();

	return (
		<InputWrapper
			className={className}
			required={required}
			label={inputLabel}
			validationError={validationError}
		>
			<Select
				value={selectValue}
				onValueChange={(val) =>
					onChange(val === EMPTY_SENTINEL ? "" : val || null)
				}
				disabled={disabled}
			>
				<SelectTrigger
					className={cn(
						triggerClassName ||
							cn(
								"w-full",
								densitySize
									? SELECT_SIZE_CLASS[densitySize]
									: "h-12"
							),
						validationError && INVALID_CONTROL_CLASS
					)}
					tabIndex={tabIndex}
					aria-label={ariaLabel}
					aria-invalid={!!validationError}
					data-testid={dataTestId}
					style={
						inputHeight ? { height: `${inputHeight}px` } : undefined
					}
				>
					<SelectValue placeholder={placeholder || inputLabel} />
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						{options
							.filter((o) => o.value != null)
							.map((option) => {
								const itemValue =
									option.value === ""
										? EMPTY_SENTINEL
										: option.value.toString();
								return (
									<SelectItem
										key={itemValue}
										value={itemValue}
										data-testid={
											option.label
												? `custom-select-${option.label
														.split(" ")
														.join("-")}`
												: "custom-select-no-label"
										}
										className="cursor-pointer hover:bg-gray-100"
									>
										{option.label}
									</SelectItem>
								);
							})}
					</SelectGroup>
				</SelectContent>
			</Select>
			{helperText && (
				<p className="mt-1 text-sm text-muted-foreground">
					{helperText}
				</p>
			)}
		</InputWrapper>
	);
};
